import { useEffect, useMemo, useState } from "react";
import { toast, Toaster } from "sonner";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/pages/admin/AdminLayout";
import ImageUploadField from "@/components/ImageUploadField";
import EmailPreviewModal from "@/components/EmailPreviewModal";

const emptyBook = { title: "", subtitle: "", description: "", cover_image: "", buy_link: "", publish_year: "", featured: false };

export default function BooksAdmin() {
  const [books, setBooks] = useState([]);
  const [editing, setEditing] = useState(null); // book id or "new"
  const [form, setForm] = useState(emptyBook);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const load = () => api.get("/books").then(({ data }) => setBooks(data));
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(emptyBook); setEditing("new"); };
  const startEdit = (b) => {
    setForm({ title: b.title, subtitle: b.subtitle || "", description: b.description, cover_image: b.cover_image, buy_link: b.buy_link || "", publish_year: b.publish_year || "", featured: !!b.featured });
    setEditing(b.id);
  };
  const cancel = () => { setEditing(null); setForm(emptyBook); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") await api.post("/books", form);
      else await api.put(`/books/${editing}`, form);
      toast.success("Saved");
      await load();
      cancel();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not save book."));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this book?")) return;
    try {
      await api.delete(`/books/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      console.error("Failed to delete book:", err);
      toast.error("Could not delete");
    }
  };

  const previewPayload = useMemo(() => ({
    title: form.title,
    subtitle: form.subtitle,
    description: form.description,
    cover_image: form.cover_image,
    buy_link: form.buy_link,
  }), [form.title, form.subtitle, form.description, form.cover_image, form.buy_link]);

  return (
    <AdminLayout title="Books">
      <Toaster position="top-center" />
      <div className="flex justify-between items-center flex-wrap gap-4">
        <p className="text-[#5C6670] font-light">Arrange your library. Drag a cover into the world.</p>
        <button onClick={startNew} data-testid="book-new" className="gv-btn-gold">+ New book</button>
      </div>

      {editing && (
        <div data-testid="book-form" className="mt-8 bg-white border border-[#C5A059] p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="gv-label">Title</label>
            <input className="gv-input mt-2" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="book-title" />
          </div>
          <div>
            <label className="gv-label">Subtitle</label>
            <input className="gv-input mt-2" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} />
          </div>
          <div>
            <label className="gv-label">Publish year</label>
            <input className="gv-input mt-2" value={form.publish_year} onChange={(e) => setForm({ ...form, publish_year: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <ImageUploadField
              value={form.cover_image}
              onChange={(url) => setForm({ ...form, cover_image: url })}
              label="Book cover"
              testIdPrefix="book-cover"
              aspect="aspect-[2/3]"
            />
            <input type="hidden" data-testid="book-cover-url" value={form.cover_image || ""} readOnly />
          </div>
          <div className="md:col-span-2">
            <label className="gv-label">Buy / read link</label>
            <input className="gv-input mt-2" value={form.buy_link} onChange={(e) => setForm({ ...form, buy_link: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <label className="gv-label">Description</label>
            <textarea rows={4} className="gv-input mt-2 resize-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="book-description" />
          </div>
          <label className="flex items-center gap-3 text-sm text-[#1A1F24]">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="book-featured" />
            <span>Featured on home page</span>
          </label>
          <div className="md:col-span-2 flex gap-3 justify-end">
            <button onClick={cancel} className="gv-btn-outline" data-testid="book-cancel">Cancel</button>
            <button
              onClick={() => setPreviewOpen(true)}
              disabled={!form.title.trim()}
              className="gv-btn-outline"
              data-testid="book-preview-email"
              title={!form.title.trim() ? "Add a title first" : "Preview the email subscribers will receive"}
            >
              Preview email
            </button>
            <button onClick={save} disabled={saving} className="gv-btn-gold" data-testid="book-save">{saving ? "Saving…" : "Save book"}</button>
          </div>
        </div>
      )}

      <EmailPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        type="book"
        payload={previewPayload}
      />

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {books.map((b) => (
          <div key={b.id} data-testid={`admin-book-${b.id}`} className="bg-white border border-[#E5E5E5] p-5 flex gap-4">
            <div className="w-20 h-28 flex-shrink-0 bg-[#F5F5F5] overflow-hidden">
              {b.cover_image && <img src={b.cover_image} alt={b.title} className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-serif text-lg text-[#1A1F24] truncate">{b.title}</div>
              <div className="text-xs text-[#5C6670] mt-1">{b.publish_year} {b.featured ? "· Featured" : ""}</div>
              <p className="text-xs text-[#5C6670] mt-2 line-clamp-2">{b.description}</p>
              <div className="flex gap-3 mt-3">
                <button onClick={() => startEdit(b)} className="text-xs tracking-[0.2em] uppercase text-[#C5A059] gv-link" data-testid={`admin-book-edit-${b.id}`}>Edit</button>
                <button onClick={() => remove(b.id)} className="text-xs tracking-[0.2em] uppercase text-[#5C6670] hover:text-red-500" data-testid={`admin-book-delete-${b.id}`}>Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}
